import { ApolloClient, HttpLink, InMemoryCache, makeVar } from '@apollo/client';
import { useMemo } from 'react';

import config from './config';

let apolloClient;

export const listFilterVar = makeVar({});
export const courseSpecialityCountVar = makeVar(0);
export const courseProfessionCountVar = makeVar(0);
export const courseLevelCountVar = makeVar(0);

export const setListFilterVar = (id, data = {}) => {
  const currentFilters = listFilterVar() || {};
  const existing = currentFilters[id] || {};

  listFilterVar({
    ...currentFilters,
    [id]: {
      filters: data.filters !== undefined ? data.filters : existing.filters,
      sort: data.sort !== undefined ? data.sort : existing.sort,
      page: data.page !== undefined ? data.page : existing.page,
      perPage: data.perPage !== undefined ? data.perPage : existing.perPage,
    },
  });
};

const mergeElasticItems = (existing, incoming, { args }) => {
  if (!incoming || !incoming.items) {
    return existing || { items: [] }; // Ensure it returns a valid structure
  }

  const page = args?.page || 1;
  const perPage = args?.perPage || 25;
  const items = existing?.items ? existing.items.slice(0) : [];

  // From args of searchPagination config
  const offset = (page - 1) * perPage;
  const end = offset + Math.min(perPage, incoming.items.length);

  // Insert the incoming elements in the right places.
  for (let i = offset; i < end; ++i) {
    items[i] = incoming.items[i - offset];
  }

  return {
    ...existing,
    ...incoming,
    items,
  };
};

const createCache = () => {
  const cacheOptions = {
    typePolicies: {
      Query: {
        fields: {
          cachedOrganizationById(_, { args, toReference }) {
            return toReference({
              __typename: 'Organization',
              document_id: args.id,
            });
          },
          cachedTaxonomyById(_, { args, toReference }) {
            return toReference({
              __typename: 'Taxonomy',
              document_id: args.id,
            });
          },
          cachedTermById(_, { args, toReference }) {
            return toReference({
              __typename: 'Term',
              document_id: args.id,
            });
          },
          cachedUserById(_, { args, toReference }) {
            return toReference({
              __typename: 'User',
              document_id: args.id,
            });
          },
          cachedUserRoleById(_, { args, toReference }) {
            return toReference({
              __typename: 'UserRole',
              document_id: args.id,
            });
          },
          organizationPagination: {
            keyArgs: false,
            merge: mergeElasticItems,
          },
          taxonomyPagination: {
            keyArgs: false,
            merge: mergeElasticItems,
          },
          termPagination: {
            keyArgs: false,
            merge: mergeElasticItems,
          },
          userPagination: {
            // keyArgs: ['boolFilter', 'sort', 'page', 'perPage'],
            merge: mergeElasticItems,
          },
          userRolePagination: {
            keyArgs: false,
            merge: mergeElasticItems,
          },
        },
      },
      CourseDescription: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      CourseEvaluation: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      CourseSubscription: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      Inspection: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      Organization: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      Page: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      Post: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      Report: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      Taxonomy: {
        keyFields: ['document_id'],
      },
      Term: {
        keyFields: ['document_id'],
      },
      User: {
        keyFields: ['document_id'],
        fields: {
          id: {
            read(_, { readField }) {
              return readField('document_id');
            },
          },
        },
      },
      UserRole: {
        keyFields: ['document_id'],
      },
    },
  };

  const cache = new InMemoryCache(cacheOptions);

  return cache;
};

function createApolloClient() {
  const fetchLink = new HttpLink({
    uri: config.apiUrl,
    credentials: 'same-origin',
  });

  return new ApolloClient({
    ssrMode: false,
    // Compose all links to chain
    link: fetchLink,
    cache: createCache(),
  });
}

export function initializeApollo(initialState = null) {
  const initializedClient = apolloClient ?? createApolloClient();

  // Create the Apollo Client once in the client
  if (!apolloClient) {
    apolloClient = initializedClient;
  }

  return initializedClient;
}

export function useApollo(initialState) {
  const store = useMemo(() => initializeApollo(initialState), [initialState]);
  return store;
}
